import {
  AuditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ContactsOutlined,
  EyeOutlined,
  FileAddOutlined,
  FileTextOutlined,
  MessageOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import {
  Badge,
  Button,
  Carousel,
  DatePicker,
  Form,
  Input,
  List,
  message,
  Spin,
  Tabs,
} from 'antd'
import { toJS } from 'mobx'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { BellLinearIcon } from '../../components/AppIcon'
import { EmptyText, UrlifiedText } from '../../components/Common/CellText'
import CounterCard from '../../components/CounterCard/CounterCard'
import EmptyContent from '../../components/EmptyContent'
import SpinnerInlineComponent from '../../components/SpinnerInlineComponent'
import { systemName, useSSO } from '../../config'
import {
  ACL_ACTION_TYPE,
  DD_MM,
  HHhMM,
  ISO_DATE_FORMAT,
  MODULE_CODE,
  NOTIFICATION_STATUS,
} from '../../constants'
import date_utils from '../../date_utils'
import DashboardLayout from '../../layouts/DashboardLayout'
import utils from '../../utils'
import {
  buildImsatWorkScheduleCompanies,
  findSelectedImsatCompany,
  getMergedLocalImsatConfig,
  mergeWorkSchedules,
} from '../../utils/imsatWorkScheduleUtils'
import validator from '../../validator'
import DashboardCalendar from './DashboardCalendar'
import { LoginWrapper } from '../PortalPageV2/PortalPageV2Styled'
import { TIN_TUC_ROUTES } from '../TinTuc/tinTucConstants'
import {
  getTinTucErrorMessage,
  logTinTucErrorInDev,
} from '../TinTuc/tinTucErrorHelper'
import {
  BannerImage,
  BannerOverlay,
  BannerSlide,
  BannerTitle,
  BannerWrapper,
  CardWrapper,
  DescriptionLink,
  NotifItem,
  NotifPanel,
  RowWrapper,
  UtilityGrid,
  UtilityHeader,
  UtilityIconWrap,
  UtilityItem,
  UtilityLabel,
  UtilitySection,
} from './DashboardPageV2Styled'
import './index.css'

const { TabPane } = Tabs

const DashboardPageV2 = propsDashboardPageV2 => {
  const {
    history,
    location,
    workScheduleStore,
    parentWorkScheduleStore,
    statisticStore,
    fileStore,
    authenticationStore,
    accountStore,
    commandStore,
    notificationStore,
    userStore,
    moduleStore,
    aclStore,
    tintucStore,
    companyStore,
    infoOptionStore,
  } = propsDashboardPageV2

  const { currentUser, isAccountAdmin, isSuperAdmin } = authenticationStore
  const { aclActionsByUser } = aclStore
  const { companyList } = companyStore
  const { sideMenuCounter, dashboardSideMenuCounter } = commandStore

  const [newsList, setNewsList] = useState([])
  const [personalNotifs, setPersonalNotifs] = useState([])
  const [workScheduleList, setWorkScheduledList] = useState([])
  const [selectedKey, setSelectedKey] = useState(null)

  const [days, setDays] = useState(date_utils.weekRange(date_utils.current()))
  const [selectedDate, setSelectedDate] = useState(
    moment().format(ISO_DATE_FORMAT)
  )
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false)
  const [isLoadingSidemenuCounter, setIsLoadingSidemenuCounter] = useState(
    false
  )
  const [isLoadingTinTuc, setIsLoadingTinTuc] = useState(false)
  const [isLoadingNotif, setIsLoadingNotif] = useState(false)
  //
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [, setShowDialog] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)

  const { CODE: optionCode } = infoOptionStore.infoOptionList || {}

  const permissionViewList = useMemo(
    () => toJS(workScheduleStore.workSchedulePermissionView),
    [workScheduleStore.workSchedulePermissionView]
  )

  const imsatCompanies = useMemo(
    () =>
      buildImsatWorkScheduleCompanies(
        infoOptionStore.infoOptionList,
        companyList,
        permissionViewList,
        currentUser?.company?.code
      ),
    [
      infoOptionStore.infoOptionList,
      companyList,
      permissionViewList,
      currentUser?.company?.code,
    ]
  )

  const mergedLocalImsatConfig = useMemo(
    () =>
      getMergedLocalImsatConfig(
        infoOptionStore.infoOptionList,
        companyList,
        selectedKey,
        currentUser?.company?.code
      ),
    [
      infoOptionStore.infoOptionList,
      companyList,
      selectedKey,
      currentUser?.company?.code,
    ]
  )

  const selectedImsatCompany = useMemo(
    () => findSelectedImsatCompany(imsatCompanies, selectedKey),
    [imsatCompanies, selectedKey]
  )

  const isImsatCompanySelected = !!selectedImsatCompany

  const isParentCompanySelected = useMemo(
    () => isImsatCompanySelected || selectedKey === optionCode,
    [isImsatCompanySelected, selectedKey, optionCode]
  )

  const workScheduleByDay = useMemo(() => {
    const scheduleByDay = {}

    workScheduleList.forEach(workSchedule => {
      const strDay = moment(workSchedule.start_at).format(DD_MM)
      if (!scheduleByDay[strDay]) {
        scheduleByDay[strDay] = []
      }
      scheduleByDay[strDay].push(workSchedule)
    })

    return scheduleByDay
  }, [workScheduleList])

  const newsListPreview = useMemo(() => newsList.slice(0, 4), [newsList])

  const weekPickerValue = useMemo(() => moment(days[0]), [days])

  const defaultActiveTabKey = useMemo(
    () =>
      days
        .map(day => day.getDay())
        .findIndex(day => day === moment().day())
        .toString(),
    [days]
  )

  const isAccessControl = useCallback(
    (moduleCode, accessControlType) => {
      if (isSuperAdmin) return true
      if (!moduleStore.checkAccessModule(moduleCode)) return false
      if (accessControlType)
        return (
          isAccountAdmin ||
          !!aclActionsByUser.find(item => item.code === accessControlType)
            ?.status
        )
      return true
    },
    [isSuperAdmin, isAccountAdmin, moduleStore, aclActionsByUser]
  )

  const handleChangeWeek = useCallback((date, type) => {
    if (type === 'prev') {
      const newDate = moment(date[0]).subtract(7, 'days')
      setDays(date_utils.weekRange(newDate))
      setSelectedDate(newDate.format(ISO_DATE_FORMAT))
    } else if (type === 'next') {
      const newDate = moment(date[0]).add(7, 'days')
      setDays(date_utils.weekRange(newDate))
      setSelectedDate(newDate.format(ISO_DATE_FORMAT))
    } else if (type === 'week') {
      const newDate = moment(date)
      setDays(date_utils.weekRange(newDate))
      setSelectedDate(newDate.format(ISO_DATE_FORMAT))
    } else if (type === 'day') {
      setSelectedDate(moment(date).format(ISO_DATE_FORMAT))
    }
  }, [])

  const handlePrevWeek = useCallback(() => handleChangeWeek(days, 'prev'), [
    days,
    handleChangeWeek,
  ])

  const handleNextWeek = useCallback(() => handleChangeWeek(days, 'next'), [
    days,
    handleChangeWeek,
  ])

  const handleWeekPickerChange = useCallback(
    date => handleChangeWeek(date, 'week'),
    [handleChangeWeek]
  )

  const renderFile = useCallback(
    file_ids => {
      if (file_ids) {
        if (file_ids.length === 0) {
          return <EmptyText>Không có tài liệu.</EmptyText>
        }
        if (file_ids.length >= 1) {
          return (
            <DescriptionLink>
              <FileTextOutlined style={{ color: '#40a9ff' }} />
              <a
                onClick={() =>
                  fileStore.handleDownloadFile(
                    file_ids[0].file_id,
                    file_ids[0].file_title
                  )
                }>
                {file_ids[0].file_title}
              </a>
            </DescriptionLink>
          )
        }
      }
    },
    [fileStore]
  )

  const getShipScheduleContent = useCallback(
    workScheduled => {
      const shipScheduleContent = []

      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Thời gian: </b>
          {workScheduled.end_at
            ? `Từ ${moment(workScheduled.start_at).format(HHhMM)} Đến ${moment(
                workScheduled.end_at
              ).format(HHhMM)}`
            : `Từ ${moment(workScheduled.start_at).format(HHhMM)}`}
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Chủ trì: </b>
          {workScheduled.host ? (
            workScheduled.host
          ) : (
            <EmptyText>Không có chủ trì.</EmptyText>
          )}
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Địa điểm: </b>
          <span>
            {workScheduled.location ? (
              UrlifiedText(workScheduled?.location)
            ) : (
              <EmptyText>Không có địa điểm.</EmptyText>
            )}
          </span>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Tài liệu: </b>
          <span>{renderFile(workScheduled?.file_ids)}</span>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Chuẩn bị: </b>
          <span>
            {workScheduled.preparation ? (
              workScheduled.preparation
            ) : (
              <EmptyText>Không có chuẩn bị.</EmptyText>
            )}
          </span>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Nội dung: </b>
          <div>
            {workScheduled.event_notice ? (
              UrlifiedText(
                workScheduled.event_notice
                  .replace(/<\/?[^>]+(>|$)/g, '')
                  .replace(/&nbsp;/g, '')
              )
            ) : (
              <EmptyText>Không có nội dung.</EmptyText>
            )}
          </div>
        </div>
      )
      shipScheduleContent.push(
        <div className="work-scheduled-item__info">
          <b>Thành viên tham gia: </b>
          <div>
            {workScheduled.attenders ? (
              workScheduled.attenders
            ) : (
              <EmptyText>Không có thành viên tham gia.</EmptyText>
            )}
          </div>
        </div>
      )

      return shipScheduleContent
    },
    [renderFile]
  )

  const onClickIncoming = useCallback(() => {
    history.push({
      pathname: `/internal-document/incoming-document`,
      state: {
        from: location.pathname,
        onlyUnread: true,
        page: 0,
        size: 10,
      },
    })
  }, [history, location.pathname])

  const onClickOutgoing = useCallback(() => {
    history.push({
      pathname: `/internal-document/outgoing-document`,
      state: {
        from: location.pathname,
        onlyUnread: true,
        page: 0,
        size: 10,
      },
    })
  }, [history, location.pathname])

  const onClickWorks = useCallback(() => {
    history.push({
      pathname: `/works`,
      search: '?is_unread=true',
      state: {
        from: location.pathname,
        isUnread: true,
      },
    })
  }, [history, location.pathname])

  const onClickMission = useCallback(() => {
    history.push({
      pathname: `/mission`,
      state: {
        from: location.pathname,
      },
    })
  }, [history, location.pathname])

  const onClickProposal = useCallback(() => {
    history.push({
      pathname: `/proposal`,
      state: {
        from: location.pathname,
        proposal_Status: 'PENDING',
      },
    })
  }, [history, location.pathname])

  const goToContentNotification = useCallback(
    async (type, id) => {
      if (!type) return

      if (type.slice(0, 5) === 'OTHER') {
        return history.push(`/proposal/other/view/${id}`)
      }

      switch (type) {
        case NOTIFICATION_STATUS.OUTGOING:
          return history.push(`/internal-document/outgoing-document/view/${id}`)
        case NOTIFICATION_STATUS.INCOMING:
        case NOTIFICATION_STATUS.INTERNAL_DOCUMENT_COMMENT:
          return history.push(`/internal-document/incoming-document/view/${id}`)
        case NOTIFICATION_STATUS.VBLT_DI:
          return history.push(`/connected-document/outgoing-document/view/${id}`)
        case NOTIFICATION_STATUS.VBLT_DEN:
          return history.push(`/connected-document/incoming-document/view/${id}`)
        case NOTIFICATION_STATUS.WORK:
          return history.push(`/works/view/${id}`)
        case NOTIFICATION_STATUS.TASK:
          return history.push(`/mission/view/${id}`)
        case NOTIFICATION_STATUS.ELEC_DOC:
          return history.push(`/internal-document/sign-document/view/${id}`)
        case NOTIFICATION_STATUS.WORK_ELEC:
          return history.push(`/archiving-n-distribution/view/${id}`)
        case NOTIFICATION_STATUS.VEHICLE:
        case NOTIFICATION_STATUS.LEAVE:
        case NOTIFICATION_STATUS.LEAVE_ANNUAL:
        case NOTIFICATION_STATUS.LEAVE_ABNORMAL:
        case NOTIFICATION_STATUS.LEAVE_NO_SALARY:
        case NOTIFICATION_STATUS.SICK:
        case NOTIFICATION_STATUS.SICK_OTHER:
        case NOTIFICATION_STATUS.OVERTIME_NORMAL:
        case NOTIFICATION_STATUS.OVERTIME_DAYOFF:
        case NOTIFICATION_STATUS.OVERTIME_HOLIDAY:
        case NOTIFICATION_STATUS.OVERTIME_CHAIRMAN:
        case NOTIFICATION_STATUS.BUSINESS_TRIP:
        case NOTIFICATION_STATUS.EVALUATE_MEMBER:
        case NOTIFICATION_STATUS.EVALUATE_LEADER:
        case NOTIFICATION_STATUS.TIMEKEEPER:
        case NOTIFICATION_STATUS.STATIONERY:
        case NOTIFICATION_STATUS.RETIMEKEEPING:
        case NOTIFICATION_STATUS.MEETING:
        case NOTIFICATION_STATUS.PROPOSAL_SALARY:
          return history.push(`/proposal/other/view/${id}`)
        case NOTIFICATION_STATUS.CONSULT:
          return history.push(`/administrative/consult/view/${id}`)
        case NOTIFICATION_STATUS.POLICY:
          return history.push(`/administrative/policy/view/${id}`)
        case NOTIFICATION_STATUS.WORK_SCHEDULE:
          return history.push(`/company-work-schedule/view/${id}`)
        case NOTIFICATION_STATUS.GENERAL:
        case NOTIFICATION_STATUS.NEWS:
          return history.push(`/utility/general-notifications/view/${id}`)
        case NOTIFICATION_STATUS.INTERNAL_MESSAGE_INCOMING:
        case NOTIFICATION_STATUS.INTERNAL_MESSAGE_INCOMING_REPLY:
          return history.push(`/utility/internal-message/receive`)
        case NOTIFICATION_STATUS.INTERNAL_MESSAGE_OUTGOING:
        case NOTIFICATION_STATUS.INTERNAL_MESSAGE_OUTGOING_REPLY:
          return history.push(`/utility/internal-message/send/view/${id}`)
        default:
          return history.push('/notification?only_news_notification=false')
      }
    },
    [history]
  )

  const renderNewsItem = useCallback(
    item => (
      <List.Item
        key={item.id}
        onClick={() => {
          history.push(TIN_TUC_ROUTES.listSelected(item.id))
        }}>
        {item.subject}
      </List.Item>
    ),
    [history]
  )

  const renderUtilities = () => {
    return (
      <UtilitySection>
        <UtilityHeader>Tiện ích</UtilityHeader>
        <UtilityGrid>
          <UtilityItem onClick={() => history.push('/utility/contacts')}>
            <UtilityIconWrap>
              <ContactsOutlined />
            </UtilityIconWrap>
            <UtilityLabel>Danh bạ</UtilityLabel>
          </UtilityItem>

          {isAccessControl(MODULE_CODE.tin_nhan_noi_bo) && (
            <UtilityItem
              onClick={() => history.push('/utility/internal-message/receive')}>
              <Badge
                count={sideMenuCounter?.unread_internal_message ?? 0}
                offset={[5, -5]}>
                <UtilityIconWrap>
                  <MessageOutlined />
                </UtilityIconWrap>
              </Badge>
              <UtilityLabel>Tin nhắn</UtilityLabel>
            </UtilityItem>
          )}

          {isAccessControl(MODULE_CODE.cong_viec) && (
            <UtilityItem
              onClick={() =>
                history.push({
                  pathname: '/works',
                  state: { from: location.pathname },
                })
              }>
              <UtilityIconWrap>
                <FileAddOutlined />
              </UtilityIconWrap>
              <UtilityLabel>Gửi đề xuất</UtilityLabel>
            </UtilityItem>
          )}

          {isAccessControl(MODULE_CODE.hanh_chinh) && (
            <UtilityItem
              onClick={() =>
                history.push({
                  pathname: '/proposal',
                  state: {
                    from: location.pathname,
                    proposal_Status: 'PENDING',
                  },
                })
              }>
              <UtilityIconWrap>
                <CalendarOutlined />
              </UtilityIconWrap>
              <UtilityLabel>Đăng ký phép</UtilityLabel>
            </UtilityItem>
          )}

          {isAccessControl(MODULE_CODE.lich_co_quan) && (
            <UtilityItem onClick={() => history.push('/company-work-schedule')}>
              <UtilityIconWrap>
                <ClockCircleOutlined />
              </UtilityIconWrap>
              <UtilityLabel>Đặt phòng họp</UtilityLabel>
            </UtilityItem>
          )}

          {isAccessControl(MODULE_CODE.phieu_rui_ro) && (
            <UtilityItem onClick={() => history.push('/risk')}>
              <UtilityIconWrap>
                <AuditOutlined />
              </UtilityIconWrap>
              <UtilityLabel>Tạo phiếu KS</UtilityLabel>
            </UtilityItem>
          )}

          {isAccessControl(MODULE_CODE.ky_so) && (
            <UtilityItem
              onClick={() => history.push('/utility/digital-signature')}>
              <UtilityIconWrap>
                <SafetyCertificateOutlined />
              </UtilityIconWrap>
              <UtilityLabel>Ký số điện tử</UtilityLabel>
            </UtilityItem>
          )}
        </UtilityGrid>
      </UtilitySection>
    )
  }

  const renderBannerCarousel = () => {
    const data = (newsList || []).slice(0, 5)

    if (!data || data.length === 0) {
      return (
        <BannerWrapper>
          <BannerSlide>
            <BannerImage $bgImage={null} />
            <BannerOverlay
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                background: 'transparent',
              }}>
              <BannerTitle style={{ fontSize: 24, textAlign: 'center' }}>
                Thông báo chung
              </BannerTitle>
              <div
                style={{
                  color: 'rgba(255,255,255,0.9)',
                  marginTop: 8,
                  fontSize: 14,
                }}>
                Chưa có thông báo mới
              </div>
            </BannerOverlay>
          </BannerSlide>
        </BannerWrapper>
      )
    }

    return (
      <BannerWrapper>
        <Carousel autoplay dots={{ className: 'slick-dots' }}>
          {data.map(item => {
            const image = item.hinhAnh
              ? `${apiUrl}/api/v1/images/${item.hinhAnh}`
              : null

            return (
              <div key={item.id}>
                <BannerSlide
                  onClick={() =>
                    history.push(TIN_TUC_ROUTES.listSelected(item.id))
                  }>
                  <BannerImage $bgImage={image} />
                  <BannerOverlay>
                    <BannerTitle>{item.subject}</BannerTitle>
                    <div
                      style={{
                        color: 'rgba(255,255,255,0.85)',
                        fontSize: 13,
                        marginTop: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                      }}>
                      <span>From {item.created_by || 'admin'}</span>
                      <span>
                        <EyeOutlined style={{ marginRight: 4 }} />
                        {item.views || 0}
                      </span>
                    </div>
                  </BannerOverlay>
                </BannerSlide>
              </div>
            )
          })}
        </Carousel>
      </BannerWrapper>
    )
  }

  const renderNotifPanel = () => {
    const notifs = (personalNotifs || []).slice(0, 4)

    return (
      <NotifPanel>
        <div className="notif-header">
          <div style={{ marginRight: 8, display: 'flex' }}>
            <BellLinearIcon style={{ fontSize: 20, color: '#1890ff' }} />
          </div>
          <span>Thông báo của tôi</span>
        </div>
        <div className="notif-list">
          <Spin spinning={isLoadingNotif} size="small">
            {notifs.length > 0 ? (
              notifs.map(item => (
                <NotifItem
                  key={item.id}
                  onClick={() => goToContentNotification(item.type, item.code)}>
                  <div className="notif-icon">
                    <BellLinearIcon style={{ fontSize: 16 }} />
                  </div>
                  <div className="notif-content">
                    <div
                      className="notif-title"
                      dangerouslySetInnerHTML={{
                        __html: item.content
                          ? item.content.trim()
                          : item.title || item.subject || 'Thông báo',
                      }}
                    />
                    <div className="notif-time">
                      {item.created_at
                        ? moment(item.created_at).format(
                            'dddd, DD [tháng] M, YYYY [lúc] HH:mm'
                          )
                        : ''}
                    </div>
                  </div>
                </NotifItem>
              ))
            ) : (
              <EmptyContent description="Không có thông báo mới" />
            )}
          </Spin>
        </div>
        <div
          className="notif-footer"
          onClick={() =>
            history.push('/notification?only_news_notification=false')
          }>
          Xem tất cả <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
        </div>
      </NotifPanel>
    )
  }

  const handleOk = useCallback(
    async values => {
      setConfirmLoading(true)
      try {
        const response = await authenticationStore.userLogin(
          values.identifier,
          values.password
        )
        if (response.status === 200) {
          const {
            data: { name_uppercase: nameInUppercase, username },
          } = await authenticationStore.checkCurrentUser()
          message.success(
            `Xin chào, ${utils.getNameInCapitalize(nameInUppercase)}!`
          )
          await Promise.allSettled([
            authenticationStore.getCurrentUserAvatar(),
            commandStore.getCommandList(),
            notificationStore.getUnreadNotificationCount(),
            notificationStore.getUnreadNewsCount(),
            userStore.getMentionUserList(),
            accountStore.getCurrentUserAccount(),
            moduleStore.getModuleList(),
            aclStore.getACLDetailByUser(username),
          ])
          setShowDialog(false)
        }
      } catch (err) {
        message.error(err?.vi || 'Đã có lỗi xảy ra!')
      } finally {
        setConfirmLoading(false)
      }
    },
    [
      authenticationStore,
      commandStore,
      notificationStore,
      userStore,
      accountStore,
      moduleStore,
      aclStore,
    ]
  )

  useEffect(() => {
    ;(async () => {
      try {
        if (currentUser) {
          setIsLoadingTinTuc(true)
          const newsRes = await tintucStore.getAllNews()
          if (newsRes && newsRes.data) {
            setNewsList(newsRes.data.data || [])
          }
          await tintucStore.getReadNewsByUsername(currentUser.username)
        }
      } catch (err) {
        logTinTucErrorInDev(err, 'DashboardPageV2.loadTinTuc')
        message.error(
          getTinTucErrorMessage(err, 'Không thể tải thông báo mới!')
        )
      } finally {
        setIsLoadingTinTuc(false)
      }

      setIsLoadingNotif(true)
      notificationStore
        .getCurrentUserNotification(false, null)
        .then(res => {
          if (res && res.data && res.data.data) {
            setPersonalNotifs(res.data.data)
          }
          setIsLoadingNotif(false)
        })
        .catch(err => {
          message.error(err?.vi || 'Đã có lỗi xảy ra!')
          setIsLoadingNotif(false)
        })
    })()
  }, [currentUser, tintucStore, notificationStore])

  useEffect(() => {
    workScheduleStore.getWorkSchedulePermissionView()
  }, [])

  useEffect(() => {
    if (currentUser?.company?.code) {
      setSelectedKey(currentUser.company.code)
    }
  }, [companyList])

  useEffect(() => {
    if (!selectedKey) return
    ;(async () => {
      try {
        setIsLoadingCalendar(true)
        const fromDate = moment(days[0]).format(ISO_DATE_FORMAT)
        const toDate = moment(days[6]).format(ISO_DATE_FORMAT)

        let data
        if (mergedLocalImsatConfig) {
          const localData = await workScheduleStore.getWorkSchedule(
            fromDate,
            toDate,
            true,
            selectedKey
          )

          let imsatData = []
          try {
            imsatData = await parentWorkScheduleStore.getWorkSchedule(
              fromDate,
              toDate,
              mergedLocalImsatConfig.imsatCode,
              mergedLocalImsatConfig.apiUrl
            )
          } catch (imsatError) {
            console.warn(
              'Không lấy được lịch IMSAT cho công ty chính',
              imsatError
            )
          }

          data = mergeWorkSchedules(localData, imsatData)
        } else if (isImsatCompanySelected) {
          data = await parentWorkScheduleStore.getWorkSchedule(
            fromDate,
            toDate,
            selectedImsatCompany.code,
            selectedImsatCompany.apiUrl
          )
        } else if (isParentCompanySelected) {
          data = await parentWorkScheduleStore.getWorkSchedule(fromDate, toDate)
        } else {
          data = await workScheduleStore.getWorkSchedule(
            fromDate,
            toDate,
            true,
            selectedKey
          )
        }

        setWorkScheduledList(data)
      } catch (error) {
        message.error(error?.vi || 'Đã có lỗi xảy ra!')
      } finally {
        setIsLoadingCalendar(false)
      }
    })()

    return () => {
      workScheduleStore.clearWorkSchedule()
      parentWorkScheduleStore.clearWorkSchedule()
    }
  }, [
    days,
    selectedKey,
    isImsatCompanySelected,
    selectedImsatCompany?.code,
    mergedLocalImsatConfig?.imsatCode,
  ])

  useEffect(() => {
    return () => {
      statisticStore.clearStatistic()
      tintucStore.clear()
      commandStore.clearStore()
    }
  }, [])

  const loginPage = useMemo(
    () => (
      <>
        <LoginWrapper
          bgImage={`${process.env.PUBLIC_URL}/assets/photos/auth-bg-2.jpg`}>
          {useSSO ? (
            <></>
          ) : (
            <Form layout={'vertical'} name={'loginForm'} onFinish={handleOk}>
              <Form.Item style={{ textAlign: 'center' }}>
                <img
                  alt="login_logo"
                  height={32}
                  src={`${process.env.PUBLIC_URL}/assets/photos/portal_logo_white.png`}
                />
              </Form.Item>
              <Form.Item
                label="Tên đăng nhập"
                name="identifier"
                rules={[
                  { required: true, message: 'Vui lòng nhập tên đăng nhập!' },
                  { validator: validator.validateUsername },
                ]}>
                <Input />
              </Form.Item>
              <Form.Item
                label="Mật khẩu"
                name="password"
                rules={[
                  { required: true, message: 'Vui lòng nhập mật khẩu!' },
                ]}>
                <Input.Password />
              </Form.Item>
              <Button
                type={'primary'}
                block
                htmlType={'submit'}
                loading={confirmLoading}>
                {' '}
                Đăng nhập
              </Button>

              <Button style={{ marginTop: 8 }} type={'link'} block>
                {' '}
                Quên mật khẩu?
              </Button>
            </Form>
          )}
        </LoginWrapper>
      </>
    ),
    [confirmLoading, handleOk]
  )

  return (
    <div>
      {currentUser != null ? (
        <DashboardLayout
          title={'Link People - Link the World'}>
          <Helmet>
            <title>Dashboard | {systemName} Portal</title>
          </Helmet>
          <div style={{ position: 'relative' }}>
            <RowWrapper>
              <div className="left-column">
                {renderBannerCarousel()}

                <CardWrapper>
                  {isAccessControl(
                    MODULE_CODE.van_ban_noi_bo,
                    ACL_ACTION_TYPE.menu__INTERNAL_DOCUMENT
                  ) && (
                    <CounterCard
                      title={'Văn bản đến chưa đọc'}
                      counter={sideMenuCounter?.unread_incoming ?? 0}
                      icon={`${process.env.PUBLIC_URL}/assets/icons/document_outgoing_new.svg`}
                      bgColor={'#e8f4ff'}
                      onClick={onClickIncoming}>
                      <SpinnerInlineComponent
                        sizeSpin={'small'}
                        isLoading={isLoadingSidemenuCounter}
                        backGround={'#ffffffc7'}
                      />
                    </CounterCard>
                  )}

                  {isAccessControl(
                    MODULE_CODE.van_ban_noi_bo,
                    ACL_ACTION_TYPE.menu__INTERNAL_DOCUMENT
                  ) && (
                    <CounterCard
                      title={'Văn bản đi chưa đọc'}
                      counter={sideMenuCounter?.unread_outgoing ?? 0}
                      icon={`${process.env.PUBLIC_URL}/assets/icons/document_incoming_new.svg`}
                      bgColor={'#fff1f0'}
                      onClick={onClickOutgoing}>
                      <SpinnerInlineComponent
                        sizeSpin={'small'}
                        isLoading={isLoadingSidemenuCounter}
                        backGround={'#ffffffc7'}
                      />
                    </CounterCard>
                  )}
                  {isAccessControl(
                    MODULE_CODE.cong_viec,
                    ACL_ACTION_TYPE.menu__WORK
                  ) && (
                    <CounterCard
                      title={'Công việc chưa đọc'}
                      counter={sideMenuCounter?.work ?? 0}
                      icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_draft.svg`}
                      bgColor={'#e6fffb'}
                      onClick={onClickWorks}>
                      <SpinnerInlineComponent
                        sizeSpin={'small'}
                        isLoading={isLoadingSidemenuCounter}
                        backGround={'#ffffffc7'}
                      />
                    </CounterCard>
                  )}
                  {isAccessControl(
                    MODULE_CODE.hanh_chinh,
                    ACL_ACTION_TYPE.menu__PROPOSAL
                  ) && (
                    <CounterCard
                      title={'Đơn hành chính chờ duyệt'}
                      counter={sideMenuCounter?.proposal_other ?? 0}
                      icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_processing.svg`}
                      bgColor={'#fff7e6'}
                      onClick={onClickProposal}>
                      <SpinnerInlineComponent
                        sizeSpin={'small'}
                        isLoading={isLoadingSidemenuCounter}
                        backGround={'#ffffffc7'}
                      />
                    </CounterCard>
                  )}
                </CardWrapper>
              </div>

              <div className="right-column">{renderNotifPanel()}</div>
            </RowWrapper>

            {renderUtilities()}

            {isAccessControl(
              MODULE_CODE.lich_co_quan,
              ACL_ACTION_TYPE.menu__WORK_SCHEDULE
            ) && (
              <>
                <Spin spinning={isLoadingCalendar}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      marginTop: 16,
                    }}>
                    <p style={{ fontWeight: 600, fontSize: 20, margin: 0 }}>
                      Lịch ngày
                    </p>
                    <DatePicker
                      allowClear={false}
                      onChange={handleWeekPickerChange}
                      picker="week"
                      placeholder={'Chọn tuần'}
                      format={date => `Tuần ${date.isoWeek()}, ${date.year()}`}
                      value={weekPickerValue}
                      style={{
                        height: 38,
                        width: 175,
                        borderRadius: 8,
                      }}
                    />
                  </div>
                  <DashboardCalendar
                    selectedDate={selectedDate}
                    data={(workScheduleList || []).filter(
                      item =>
                        moment(item.start_at).format(ISO_DATE_FORMAT) ===
                        selectedDate
                    )}
                    selectedEventId={selectedEvent?.id}
                    onEventClick={setSelectedEvent}
                    isShowPopoverContent
                    onChangeDate={date => handleChangeWeek(date, 'day')}
                  />
                </Spin>
              </>
            )}
            {/* {updatePassword} */}
          </div>
        </DashboardLayout>
      ) : (
        loginPage
      )}
    </div>
  )
}

export default memo(
  inject(
    'workScheduleStore',
    'parentWorkScheduleStore',
    'loadingAnimationStore',
    'commonStore',
    'statisticStore',
    'fileStore',
    'authenticationStore',
    'accountStore',
    'userStore',
    'companyStore',
    'notificationStore',
    'commandStore',
    'taskManagementStore',
    'taskManagementCommentStore',
    'moduleStore',
    'tintucStore',
    'aclStore',
    'infoOptionStore'
  )(observer(DashboardPageV2))
)
